import i18next from 'i18next';
import { isEmpty } from 'lodash-es';
import { DateTime } from 'luxon';
import moment from 'moment';
import React from 'react';
import { CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';

import {
  formatTooltipDate,
  formatTooltipValue,
  getScalesInfo,
  IDashboardSubChartMetric,
} from '~/src/dash/components/charts/common';
import { LoadingChart } from '~/src/dash/components/charts/LoadingChart';
import { Col, Cols, fontSizing, Row, Rows, spacing, Text, usePanel } from '~/src/dash/publicaUI';
import { convertLargeNumbersToRoundedString } from '~/src/dash/utils/basic';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';

const renderItems = (metrics: IDashboardSubChartMetric[], numItems: number) => {
  return metrics.map((metric) => {
    const yAxisId = metric.yScale || 'base';
    return (
      <Line
        name={metric.label}
        type='monotone'
        dataKey={metric.key}
        stroke={metric.color}
        fill={metric.color}
        strokeWidth={3}
        activeDot={{ r: 4 }}
        dot={numItems <= 15}
        yAxisId={yAxisId}
      />
    );
  });
};

interface ICustomTooltipProps {
  active: boolean;
  payload: any;
  label: string;
  metricsConfig: Record<string, IDashboardSubChartMetric>;
  grouping: number;
  currencySymbol: string;
  language?: string;
}

const CustomTooltip = ({
  active,
  payload,
  metricsConfig,
  currencySymbol,
  language = 'en',
}: ICustomTooltipProps): React.JSX.Element | null => {
  if (!active || isEmpty(payload)) return null;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const date = formatTooltipDate(payload[0].payload.Date as string | moment.Moment, 0, language);
  return (
    <Rows
      style={{
        backgroundColor: publicaColors.white,
        border: `1px solid ${publicaColors.lightGray}`,
        padding: spacing.small,
      }}
    >
      <Row
        noPadding
        style={{
          borderBottom: `1px solid ${publicaColors.lightGray}`,
          marginBottom: spacing.small,
        }}
      >
        <Text small bold>
          {date}
        </Text>
      </Row>
      {payload.map((item: any) => {
        const { dataKey, name } = item;
        const valueType = metricsConfig[dataKey].valueType;
        const value = formatTooltipValue(item.payload[dataKey], valueType, currencySymbol);
        return (
          <Row noPadding>
            <Cols
              gap={spacing.extraLarge}
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Col>
                <Cols gap='4px' style={{ alignItems: 'center' }}>
                  <Col paddingBottom='1px'>
                    <Text color={item.fill} style={{ fontSize: fontSizing.mini }}>
                      ⬤
                    </Text>
                  </Col>
                  <Col>
                    <Text small bold>
                      {name}
                    </Text>
                  </Col>
                </Cols>
              </Col>
              <Col>
                <Text small>{value}</Text>
              </Col>
            </Cols>
          </Row>
        );
      })}
    </Rows>
  );
};

const formatTick = (grouping: number | undefined, date: string | moment.Moment): string => {
  const luxonDate = DateTime.fromISO(moment(date).utc().toISOString()).setLocale(
    i18next?.language || 'en',
  );
  return grouping && date
    ? luxonDate.toLocaleString({
        ...DateTime.DATE_SHORT,
        year: undefined,
        timeZone: 'UTC',
      })
    : luxonDate.toLocaleString({ ...DateTime.TIME_24_SIMPLE, timeZone: 'UTC' });
};

interface IMultiLineChartProps {
  loadState: LoadingState;
  data: Array<Record<string, any>>;
  grouping?: number;
  metrics: IDashboardSubChartMetric[];
  metricsCfg: Record<string, IDashboardSubChartMetric>;
  syncID?: string;
}

export const MultiLineChart = ({
  loadState,
  data = [],
  metrics,
  metricsCfg,
  grouping,
  syncID = '',
}: IMultiLineChartProps) => {
  const { width } = usePanel();
  const { currencySymbol } = useBaseStore();
  const { scales, numVisibleAxes } = getScalesInfo(metrics);

  const formatTimestamp = (date: string | moment.Moment): string => {
    return !date ? '' : formatTick(grouping, date);
  };

  return (
    <div
      style={{
        width: '100%',
        height: '280px',
      }}
    >
      {loadState === LoadingStateLoading ? (
        <LoadingChart />
      ) : (
        <ComposedChart
          width={(width || 0) - 80}
          height={280}
          data={data}
          barGap={4}
          margin={{
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
          }}
          syncId={syncID}
        >
          <Tooltip
            content={
              // this bypasses an issue with automated props from recharts
              // @ts-ignore
              <CustomTooltip
                metricsConfig={metricsCfg}
                grouping={grouping || 0}
                currencySymbol={currencySymbol}
                language={i18next.language}
              />
            }
            cursor={{ fill: publicaColors.lightGray }}
          />
          <CartesianGrid strokeDasharray='3 3' vertical={false} />
          {renderItems(metrics, data.length)}
          <XAxis
            dataKey='Date'
            tick={{
              fontSize: 12,
              fill: 'black',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
            // @ts-ignore
            tickFormatter={formatTimestamp}
            domain={[0, data.length]}
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            tick={{ fontSize: 12, fill: 'black', textAnchor: 'end' }}
            tickFormatter={convertLargeNumbersToRoundedString}
            yAxisId='base'
            hide={scales.base.hide}
            orientation='left'
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            tick={{
              fontSize: 12,
              fill: 'black',
              textAnchor: scales.large.orientation === 'left' ? 'end' : 'start',
            }}
            tickFormatter={convertLargeNumbersToRoundedString}
            yAxisId='large'
            hide={scales.large.hide}
            orientation={scales.large.orientation}
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            tick={{
              fontSize: 12,
              fill: 'black',
              textAnchor: scales.small.orientation === 'left' ? 'end' : 'start',
            }}
            tickFormatter={convertLargeNumbersToRoundedString}
            yAxisId='small'
            hide={scales.small.hide}
            orientation={scales.small.orientation}
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            yAxisId='placeholder-left'
            hide={metrics.length !== 0}
            orientation='left'
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            yAxisId='placeholder-right'
            hide={numVisibleAxes > 1}
            orientation='right'
          />
        </ComposedChart>
      )}
    </div>
  );
};
